import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Lock, Eye, FileText, CheckCircle2, Phone, Mail, MapPin } from "lucide-react";
import { CLINIC, telLink, whatsappLink } from "@/lib/site";
import { Footer } from "@/components/site/Footer";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col justify-between">
      {/* Header Bar */}
      <header className="sticky top-0 z-40 bg-background/90 backdrop-blur-md border-b border-border">
        <div className="container-x h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img src={CLINIC.logo} alt="FizioFit Logo" className="h-9 sm:h-10 w-auto object-contain" />
            <span className="font-medium tracking-tight text-lg text-foreground">
              FizioFit<span className="text-accent font-light"> Center</span>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors"
          >
            <ArrowLeft size={16} /> Kthehu te Kryefaqja
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="container-x py-12 md:py-20 max-w-4xl mx-auto flex-1">
        {/* Title Section */}
        <div className="border-b border-border pb-8 mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-accent/10 text-accent text-xs font-medium mb-4">
            <Shield size={14} /> Politika e Privatësisë & Mbrojtja e të Dhënave
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-light tracking-tight text-foreground">
            Politika e Privatësisë
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Përditësuar së fundmi më: {new Date().toLocaleDateString("sq-AL", { year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>

        {/* Content Blocks */}
        <div className="space-y-10 text-[15px] leading-relaxed text-foreground/90 font-normal">
          {/* 1. Hyrje */}
          <section className="space-y-3">
            <h2 className="text-xl sm:text-2xl font-medium tracking-tight text-foreground flex items-center gap-2.5">
              <span className="text-accent text-lg">01.</span> Hyrje dhe Përmbledhje
            </h2>
            <p className="text-muted-foreground">
              Në <strong>{CLINIC.name}</strong> (në vijim referuar si "Klinika", "Ne", ose "FizioFit"), privatësia dhe konfidencialiteti i të dhënave personale dhe shëndetësore të pacientëve tanë janë parësore. Kjo Politikë Privatësie shpjegon se si mbledhim, përdorim, ruajmë dhe mbrojmë informacionin tuaj kur vizitoni faqen tonë të internetit ose kur rezervoni një shërbim fizioterapie pranë qendrës sonë në Lushnjë.
            </p>
            <p className="text-muted-foreground">
              Ne respektojmë rregulloret e zbatueshme për mbrojtjen e të dhënave personale në Republikën e Shqipërisë (Ligji Nr. 9887, datë 10.03.2008 "Për Mbrojtjen e të Dhënave Personale") si dhe parimet e përgjithshme ndërkombëtare të privatësisë (GDPR).
            </p>
          </section>

          {/* 2. Çfarë të dhënash mbledhim */}
          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-medium tracking-tight text-foreground flex items-center gap-2.5">
              <span className="text-accent text-lg">02.</span> Të Dhënat që Mbledhim
            </h2>
            <p className="text-muted-foreground">
              Kur përdorni formularin tonë të rezervimit ose na kontaktoni në mënyrë të drejtpërdrejtë, ne mund të mbledhim informacionet e mëposhtme:
            </p>
            <ul className="grid sm:grid-cols-2 gap-3 pt-1">
              <li className="p-4 rounded-sm border border-border bg-card flex items-start gap-3">
                <CheckCircle2 size={18} className="text-accent shrink-0 mt-0.5" />
                <div>
                  <strong className="text-foreground text-sm block">Të Dhëna Identifikimi</strong>
                  <span className="text-xs text-muted-foreground">Emri, Mbiemri, dhe Numri i Telefonit.</span>
                </div>
              </li>
              <li className="p-4 rounded-sm border border-border bg-card flex items-start gap-3">
                <CheckCircle2 size={18} className="text-accent shrink-0 mt-0.5" />
                <div>
                  <strong className="text-foreground text-sm block">Detajet e Takimit</strong>
                  <span className="text-xs text-muted-foreground">Shërbimi i kërkuar, data dhe ora e preferuar.</span>
                </div>
              </li>
              <li className="p-4 rounded-sm border border-border bg-card flex items-start gap-3">
                <CheckCircle2 size={18} className="text-accent shrink-0 mt-0.5" />
                <div>
                  <strong className="text-foreground text-sm block">Përshkrimi i Gjendjes</strong>
                  <span className="text-xs text-muted-foreground">Mesazhi ose informacioni mbi problemin fizik që ndani vullnetarisht.</span>
                </div>
              </li>
              <li className="p-4 rounded-sm border border-border bg-card flex items-start gap-3">
                <CheckCircle2 size={18} className="text-accent shrink-0 mt-0.5" />
                <div>
                  <strong className="text-foreground text-sm block">Të Dhëna Teknike Bazë</strong>
                  <span className="text-xs text-muted-foreground">Të dhëna standarde të shfletuesit për sigurinë dhe funksionimin e faqes.</span>
                </div>
              </li>
            </ul>
          </section>

          {/* 3. Përse i përdorim të dhënat */}
          <section className="space-y-3">
            <h2 className="text-xl sm:text-2xl font-medium tracking-tight text-foreground flex items-center gap-2.5">
              <span className="text-accent text-lg">03.</span> Qëllimi i Përpunimit të të Dhënave
            </h2>
            <p className="text-muted-foreground">Të dhënat e mbledhura përdoren ekskluzivisht për:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-muted-foreground">
              <li>Përpunimin, konfirmimin dhe menaxhimin e kërkesave tuaja për takime fizioterapie.</li>
              <li>Komunikimin me ju nëpërmjet telefonit ose WhatsApp për të caktuar ose ndryshuar oraret.</li>
              <li>Përgatitjen e protokollit të përshtatshëm të trajtimit sipas nevojave tuaja specifike.</li>
              <li>Përmirësimin e cilësisë së shërbimit dhe eksperiencës së pacientit.</li>
            </ul>
          </section>

          {/* 4. Konfidencialiteti Mjekësor */}
          <section className="p-6 rounded-sm border border-accent/30 bg-accent/5 space-y-3">
            <h2 className="text-xl font-medium tracking-tight text-foreground flex items-center gap-2">
              <Lock size={18} className="text-accent" /> Konfidencialiteti dhe Siguria e të Dhënave
            </h2>
            <p className="text-sm text-muted-foreground">
              Informacioni shëndetësor dhe personal trajtohet nën rregulla strikte të <strong>sekretit profesional mjekësor</strong>. Ne <strong>NUK</strong> i shesim, nuk i japim me qira dhe nuk i ndajmë të dhënat tuaja me asnjë palë të tretë për qëllime marketingu apo reklame.
            </p>
          </section>

          {/* 5. Ruajtja dhe Fshirja e të Dhënave */}
          <section className="space-y-3">
            <h2 className="text-xl sm:text-2xl font-medium tracking-tight text-foreground flex items-center gap-2.5">
              <span className="text-accent text-lg">04.</span> Kohëzgjatja e Ruajtjes së të Dhënave
            </h2>
            <p className="text-muted-foreground">
              Të dhënat tuaja ruhen vetëm aq kohë sa është e nevojshme për përmbushjen e qëllimit mjekësor dhe administrativ të shërbimit. Në çdo moment, ju keni të drejtë të kërkoni fshirjen e plotë të historikut tuaj të kontaktit nga sistemet tona.
            </p>
          </section>

          {/* 6. Të Drejtat Tuaja */}
          <section className="space-y-3">
            <h2 className="text-xl sm:text-2xl font-medium tracking-tight text-foreground flex items-center gap-2.5">
              <span className="text-accent text-lg">05.</span> Të Drejtat Tuaja Ligjore
            </h2>
            <p className="text-muted-foreground">Sipas ligjit për mbrojtjen e të dhënave, ju gëzoni të drejtat e mëposhtme:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-muted-foreground">
              <li><strong>E drejta për informim dhe akses:</strong> Të dini se çfarë të dhënash ruajmë për ju.</li>
              <li><strong>E drejta për korrigjim:</strong> Të kërkoni përmirësimin ose përditësimin e të dhënave të pasakta.</li>
              <li><strong>E drejta për fshirje ("Të drejtën për t'u harruar"):</strong> Të kërkoni heqjen e të dhënave tuaja nga regjistrat tanë.</li>
              <li><strong>E drejta për kufizim të përpunimit:</strong> Të kërkoni pezullimin e përpunimit të të dhënave tuaja personale.</li>
            </ul>
          </section>

          {/* 7. Cookies dhe Shërbimet e Jashtme */}
          <section className="space-y-3">
            <h2 className="text-xl sm:text-2xl font-medium tracking-tight text-foreground flex items-center gap-2.5">
              <span className="text-accent text-lg">06.</span> Shërbimet e Jashtme & Hartat (Google Maps)
            </h2>
            <p className="text-muted-foreground">
              Faqja jonë integron Google Maps për t'ju ndihmuar të gjeni vendndodhjen fizike të klinikës në Lushnjë. Ndërveprimi juaj me hartën e Google i nënshtrohet gjithashtu politikave përkatëse të privatësisë së Google.
            </p>
          </section>

          {/* 8. Kontakt */}
          <section className="pt-6 border-t border-border space-y-4">
            <h2 className="text-xl sm:text-2xl font-medium tracking-tight text-foreground flex items-center gap-2.5">
              <span className="text-accent text-lg">07.</span> Si të na Kontaktoni
            </h2>
            <p className="text-muted-foreground">
              Për çdo pyetje, kërkesë apo paqartësi lidhur me këtë Politikë Privatësie ose trajtimin e të dhënave tuaja, mund të na kontaktoni lirisht:
            </p>
            <div className="grid sm:grid-cols-2 gap-3 pt-2">
              <div className="p-4 rounded-sm border border-border bg-card">
                <div className="text-xs text-muted-foreground mb-1">Klinika Fizike</div>
                <div className="text-sm font-medium text-foreground">{CLINIC.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{CLINIC.address}</div>
              </div>
              <div className="p-4 rounded-sm border border-border bg-card">
                <div className="text-xs text-muted-foreground mb-1">Telefon & WhatsApp</div>
                <a href={telLink} className="text-sm font-medium text-accent hover:underline block">{CLINIC.phone}</a>
                <div className="text-xs text-muted-foreground mt-0.5">Orari: {CLINIC.hours} (Çdo ditë)</div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
